import { create } from "zustand";
import { isReplyNotification, type Notification } from "~/types/notification";

const PENDING_TOAST_QUEUE_MAX = 10;

type NotificationState = {
  notifications: Notification[];
  unreadCount: number;
  toastQueue: Notification[];
  /** 通常通知の保留キュー。先頭が最新。5秒ごとに先頭1件を toastQueue へ移動する */
  pendingToastQueue: Notification[];
};

type NotificationActions = {
  addNotification: (notification: Notification) => void;
  markAsRead: (id: string) => void;
  clearAll: () => void;
  enqueueToast: (notification: Notification) => void;
  dequeueToast: () => void;
  clearToastQueue: () => void;
  /** 通常通知を保留キューの先頭に追加する。最大件数超過時は末尾（古いもの）から切り捨て */
  addToPendingQueue: (notification: Notification) => void;
  /** 保留キューの先頭1件を toastQueue に移動する */
  flushPendingToast: () => void;
};

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  toastQueue: [],
  pendingToastQueue: [],
};

export const useNotificationStore = create<
  NotificationState & NotificationActions
>()((set) => ({
  ...initialState,

  addNotification: (notification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: notification.isRead
        ? state.unreadCount
        : state.unreadCount + 1,
    })),

  markAsRead: (id) =>
    set((state) => {
      const target = state.notifications.find((n) => n.id === id);
      if (!target || target.isRead) return state;
      return {
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, isRead: true } : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    }),

  clearAll: () =>
    set({
      notifications: [],
      unreadCount: 0,
    }),

  enqueueToast: (notification) =>
    set((state) => {
      if (isReplyNotification(notification)) {
        const insertAt = Math.min(1, state.toastQueue.length);
        const next = [...state.toastQueue];
        next.splice(insertAt, 0, notification);
        return { toastQueue: next };
      }
      return { toastQueue: [...state.toastQueue, notification] };
    }),

  dequeueToast: () =>
    set((state) => ({
      toastQueue: state.toastQueue.slice(1),
    })),

  clearToastQueue: () => set({ toastQueue: [] }),

  addToPendingQueue: (notification) =>
    set((state) => ({
      pendingToastQueue: [notification, ...state.pendingToastQueue].slice(
        0,
        PENDING_TOAST_QUEUE_MAX,
      ),
    })),

  flushPendingToast: () =>
    set((state) => {
      if (state.pendingToastQueue.length === 0) return state;
      const [head, ...rest] = state.pendingToastQueue;
      return {
        toastQueue: [...state.toastQueue, head],
        pendingToastQueue: rest,
      };
    }),
}));
